import React from "react";
import Button from "@material-ui/core/Button";
import format from "date-fns/format";
import getISOWeek from "date-fns/getISOWeek";

const formatDisplay = (date: Date | null, placeholderText: string): string => {
    if (!date) return placeholderText;
    try {
        return format(new Date(date), "dd-MM-yyyy");
    } catch {
        return placeholderText;
    }
};

type DatePickerProps = {
    name?: string;
    isClearable?: boolean;
    selected: Date | null;
    onChange: (date: Date | null) => void;
    disabled?: boolean;
    width?: string;
    locale?: string;
    showWeekNumbers?: boolean;
    dateFormat?: string;
    placeholderText?: string;
    customInput?: React.ReactElement;
    open?: boolean;
    showPopperArrow?: boolean;
    showMonthDropdown?: boolean;
    showYearDropdown?: boolean;
    dropdownMode?: "select" | "scroll";
    renderCustomHeader?: (props: any) => React.ReactNode;
    minDate?: Date;
    maxDate?: Date;
    startDate?: Date;
    endDate?: Date;
    selectsStart?: boolean;
    selectsEnd?: boolean;
    className?: string;
};

const DatePicker: React.FC<DatePickerProps> = ({
    selected,
    onChange,
    disabled = false,
    width = "300px",
    placeholderText = "Please select a date",
    customInput,
    name,
    isClearable = false,
    className,
    renderCustomHeader,
    open: externalOpen,
}) => {
    const [internalOpen, setInternalOpen] = React.useState(false);
    const [month, setMonth] = React.useState(selected ? new Date(selected) : new Date());
    const containerRef = React.useRef<HTMLDivElement>(null);

    const isOpen = externalOpen ? externalOpen : internalOpen;

    React.useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setInternalOpen(false);
            }
        };
        if (isOpen) document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [isOpen]);

    const year = month.getFullYear();
    const monthIndex = month.getMonth();

    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, monthIndex, 0).getDate();
    const firstDayOfWeek = (new Date(year, monthIndex, 1).getDay() + 6) % 7;

    const weeks: { day: number; currentMonth: boolean }[][] = [];
    let week: { day: number; currentMonth: boolean }[] = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
        week.push({ day: daysInPrevMonth - firstDayOfWeek + 1 + i, currentMonth: false });
    }

    for (let d = 1; d <= daysInMonth; d++) {
        week.push({ day: d, currentMonth: true });
        if (week.length === 7) {
            weeks.push(week);
            week = [];
        }
    }

    if (week.length > 0) {
        let nextDay = 1;
        while (week.length < 7) {
            week.push({ day: nextDay++, currentMonth: false });
        }
        weeks.push(week);
    }

    const selectDay = (day: number, currentMonth: boolean) => {
        let newDate: Date;
        if (currentMonth) {
            newDate = new Date(year, monthIndex, day, 12, 0, 0);
        } else if (weeks[0] && weeks[0][0].day === day && !weeks[0][0].currentMonth && weeks[0][0].day > 15) {
            newDate = new Date(year, monthIndex - 1, day, 12, 0, 0);
            setMonth(new Date(year, monthIndex - 1));
        } else {
            newDate = new Date(year, monthIndex + 1, day, 12, 0, 0);
            setMonth(new Date(year, monthIndex + 1));
        }

        onChange(newDate);
        setInternalOpen(false);
    };

    const prevMonth = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setMonth(new Date(year, monthIndex - 1));
    };

    const nextMonth = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setMonth(new Date(year, monthIndex + 1));
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    const monthLabel = month.toLocaleString("default", { month: "long", year: "numeric" });
    const displayValue = formatDisplay(selected, placeholderText);
    const DAY_HEADERS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

    const isSelectedDay = (day: number, currentMonth: boolean) => {
        if (!selected || !currentMonth) return false;
        return selected.getDate() === day && selected.getMonth() === monthIndex && selected.getFullYear() === year;
    };

    return (
        <div ref={containerRef} style={{ position: "relative", display: "inline-block", marginTop: "5px", width }} className={className}>
            {customInput ? (
                React.cloneElement(customInput, {
                    onClick: () => !disabled && setInternalOpen(!internalOpen),
                    value: displayValue,
                    disabled,
                    selectedDate: selected,
                })
            ) : (
                <Button
                    type="button"
                    name={name || "CalenderCustomInput"}
                    variant="contained"
                    color="primary"
                    onClick={() => !disabled && setInternalOpen(!internalOpen)}
                    disabled={disabled}
                    style={{ width }}
                >
                    {displayValue}
                </Button>
            )}

            {isClearable && selected && (
                <Button
                    type="button"
                    size="small"
                    style={{
                        right: -2,
                        minWidth: 20,
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        padding: 0,
                        zIndex: 1,
                        fontSize: "16px",
                    }}
                    variant="contained"
                    color="secondary"
                    onClick={handleClear}
                    disabled={disabled}
                >
                    ×
                </Button>
            )}

            {isOpen && (
                <div
                    style={{
                        position: "absolute",
                        top: "45px",
                        left: 0,
                        background: "white",
                        border: "1px solid #d0d0d0",
                        borderRadius: "8px",
                        zIndex: 1000,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                        fontFamily: "inherit",
                        overflow: "hidden",
                        minWidth: "300px",
                    }}
                >
                    {renderCustomHeader ? (
                        renderCustomHeader({
                            date: month,
                            changeYear: (y: number) => setMonth(new Date(y, monthIndex)),
                            changeMonth: (m: number) => setMonth(new Date(year, m)),
                            decreaseMonth: prevMonth,
                            increaseMonth: nextMonth,
                            prevMonthButtonDisabled: false,
                            nextMonthButtonDisabled: false,
                        })
                    ) : (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                background: "#f0f0f0",
                                padding: "10px 12px",
                            }}
                        >
                            <button
                                type="button"
                                onClick={prevMonth}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    color: "#888",
                                    padding: "2px 8px",
                                    lineHeight: 1,
                                }}
                            >
                                ◀
                            </button>
                            <strong style={{ fontSize: "14px", color: "#333", fontWeight: 700 }}>{monthLabel}</strong>
                            <button
                                type="button"
                                onClick={nextMonth}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    color: "#888",
                                    padding: "2px 8px",
                                    lineHeight: 1,
                                }}
                            >
                                ▶
                            </button>
                        </div>
                    )}

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "40px repeat(7, 1fr)",
                            background: "#f0f0f0",
                            borderBottom: "1px solid #d0d0d0",
                            padding: "4px 8px",
                        }}
                    >
                        <div style={{ textAlign: "center", fontSize: "13px", color: "#aaa", fontWeight: 600, padding: "4px 0" }}>#</div>
                        {DAY_HEADERS.map((d) => (
                            <div
                                key={d}
                                style={{
                                    textAlign: "center",
                                    fontSize: "13px",
                                    color: "#555",
                                    fontWeight: 600,
                                    padding: "4px 0",
                                }}
                            >
                                {d}
                            </div>
                        ))}
                    </div>

                    <div style={{ padding: "4px 8px 8px 8px" }}>
                        {weeks.map((wk, wi) => {
                            const firstCurrent = wk.find((c) => c.currentMonth);
                            const weekNum = firstCurrent ? getISOWeek(new Date(year, monthIndex, firstCurrent.day)) : getISOWeek(new Date(year, monthIndex - 1, wk[0].day));

                            return (
                                <div
                                    key={wi}
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "40px repeat(7, 1fr)",
                                        marginBottom: "2px",
                                    }}
                                >
                                    <div
                                        style={{
                                            textAlign: "center",
                                            fontSize: "12px",
                                            color: "#bbb",
                                            padding: "6px 0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {weekNum}
                                    </div>

                                    {wk.map((cell, di) => {
                                        const sel = isSelectedDay(cell.day, cell.currentMonth);
                                        return (
                                            <div
                                                key={di}
                                                onClick={() => selectDay(cell.day, cell.currentMonth)}
                                                style={{
                                                    textAlign: "center",
                                                    padding: "6px 2px",
                                                    cursor: "pointer",
                                                    borderRadius: "4px",
                                                    background: sel ? "#1976d2" : "transparent",
                                                    color: sel ? "white" : cell.currentMonth ? "#333" : "#bbb",
                                                    fontWeight: sel ? 600 : 400,
                                                    fontSize: "13px",
                                                    userSelect: "none",
                                                }}
                                            >
                                                {cell.day}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;
